import { Box, Card, useTheme } from '@mui/material';
import React from 'react';
import { getInfoCardBackground } from './infoCardBackground.util';
import type { BackgroundStyles } from './infoCard.types';

export interface InfoCardContainerProps {
  children: React.ReactNode;
  backgroundStyle?: BackgroundStyles;
  shouldHighlight?: boolean;
}

/**
 * InfoCardContainer renders the outer card chrome and manages highlight animation.
 * @param props - Card content, background variant, and highlight state.
 * @returns A themed card container.
 */
export const InfoCardContainer = ({
  children,
  backgroundStyle = 'default',
  shouldHighlight,
}: InfoCardContainerProps) => {
  const theme = useTheme();
  const cardRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const cardElement = cardRef.current;
    if (!cardElement || !shouldHighlight) {
      return undefined;
    }

    const animation = cardElement.animate(
      [
        { borderColor: 'rgba(31, 92, 105, 0.772)' },
        { borderColor: 'rgba(31, 92, 105, 0)' },
        { borderColor: 'rgba(31, 92, 105, 0.772)' },
      ],
      {
        duration: 1000,
        easing: 'ease-in',
        iterations: Infinity,
      }
    );

    return () => {
      animation.cancel();
    };
  }, [shouldHighlight]);

  return (
    <Box
      ref={cardRef}
      component={Card}
      boxSizing="border-box"
      fontSize="12px"
      border={`1px solid ${theme.palette.divider}`}
      boxShadow="none"
      bgcolor={getInfoCardBackground(backgroundStyle)}
    >
      {children}
    </Box>
  );
};
